/**
 * Starfield Component
 * WebGPU starfield using an instanced Sprite + PointsNodeMaterial (TSL).
 *
 * NOTE: a plain <points> object is NOT used here. In r183 the WebGPU
 * PointsNodeMaterial only applies `size` / `sizeAttenuation` on the SPRITE path
 * (isSprite), not the <points> path — there the native point size is clamped to
 * ~1px, so stars become invisible (worse at 4K). Rendering as an instanced
 * Sprite (count = star count, positionNode = per-instance position) makes the
 * size controls work, and sizeAttenuation:false keeps a constant on-screen size
 * regardless of zoom.
 */

import { useRef, useMemo, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { PointsNodeMaterial } from 'three/webgpu'
import { instancedBufferAttribute, instanceIndex, hash, time, sin, float, vec3, mix, step, uniform } from 'three/tsl'

export function Starfield({
  count = 5000,
  radius = 200,
  rotationSpeed = 0.0001,
  size = 3,
  dense = false,
  sizeScaleRef = null,
  // Number of ALWAYS-visible stars (≤ count * 2). Stars beyond it only fade in
  // with `dense`. A uniform, not a geometry rebuild, so it can vary per
  // breakpoint tier (denser base field on 4K) without the rebuild-mid-submit
  // renderer crash described below for `size`.
  baseVisibleCount = count,
}) {
  const groupRef = useRef()

  // Always generate the full (2x) star set once and always DRAW all of them; the
  // stars beyond baseVisibleCount have their opacity faded via a uniform so
  // density ramps in smoothly when zooming into a location (no reshuffle, no
  // pop-in).
  const maxCount = count * 2
  const fadeRef = useRef(uniform(0)) // 0 = extra stars hidden, 1 = fully shown
  const baseCountRef = useRef(uniform(Math.min(baseVisibleCount, maxCount)))
  // `size` is a uniform, NOT a useMemo dep, so changing it (e.g. on a breakpoint
  // change — app.jsx sets a different star size per tier) updates the shader in
  // place instead of rebuilding the sprite. Rebuilding disposed the geometry
  // buffer synchronously during a React commit while the WebGPU render loop was
  // mid-submit → "[Buffer] used in submit while destroyed", which killed the whole
  // renderer on every breakpoint change.
  const sizeRef = useRef(uniform(size))

  // Random star positions in a sphere shell, as a per-instance position buffer
  const positions = useMemo(() => {
    const arr = new Float32Array(maxCount * 3)
    for (let i = 0; i < maxCount; i++) {
      const i3 = i * 3
      const theta = Math.random() * Math.PI * 2
      const phi = Math.acos(2 * Math.random() - 1)
      const r = radius * (0.5 + Math.random() * 0.5)
      arr[i3] = r * Math.sin(phi) * Math.cos(theta)
      arr[i3 + 1] = r * Math.sin(phi) * Math.sin(theta)
      arr[i3 + 2] = r * Math.cos(phi)
    }
    return arr
  }, [maxCount, radius])

  // Instanced sprite: holds all maxCount stars; visible count toggled below.
  const sprite = useMemo(() => {
    const mat = new PointsNodeMaterial()
    mat.transparent = true
    mat.depthWrite = false
    // Constant on-screen size — far backdrop, must not shrink/disappear on zoom.
    mat.sizeAttenuation = false
    mat.positionNode = instancedBufferAttribute(new THREE.InstancedBufferAttribute(positions, 3))

    // Per-instance pseudo-random values (stable per star)
    const rSize   = hash(instanceIndex)                 // size variation
    const rBright = hash(instanceIndex.add(101))        // brightness variation
    const rTwink  = hash(instanceIndex.add(977))        // twinkle selector + phase

    // Size: 35%–100% of the configured max, randomised per star. The base size is
    // a uniform multiplied by a `sizeScale` uniform (default 1) so external code
    // (the chase camera) can temporarily shrink the stars just for its render —
    // bracketing gl.render the same way it overrides Earth opacity. userData
    // exposes the scale uniform for that.
    const sizeScaleU = uniform(1)
    mat.sizeNode = sizeRef.current.mul(mix(0.35, 1.0, rSize)).mul(sizeScaleU)
    mat.userData.sizeScaleUniform = sizeScaleU

    // Brightness: most stars sit near 1.0; a subset are pushed into HDR (>1) so
    // they exceed the bloom and glow. Twinkle: only the ~4% brightest pulse.
    const baseBright = mix(0.7, 1.15, rBright)
    // a few extra-bright "hero" stars (top ~8%) get an HDR boost for bloom
    const heroBoost  = step(0.92, rBright).mul(1.6)
    // twinkle gate: only stars with rTwink > 0.96 twinkle (~4%)
    const twinkleGate = step(0.96, rTwink)
    const twinkle = sin(time.mul(mix(1.5, 4.0, rTwink)).add(rTwink.mul(6.283))).mul(0.5).add(0.5) // 0..1
    const twinkleAmt = mix(float(1.0), mix(0.4, 1.4, twinkle), twinkleGate)

    const brightness = baseBright.add(heroBoost).mul(twinkleAmt)

    // Stars below the baseVisibleCount boundary (a uniform, tier-dependent) are
    // always full opacity; the rest fade in/out via fadeRef so the extra density
    // ramps smoothly when zooming.
    const isExtraStar = step(baseCountRef.current, float(instanceIndex))
    const fade = mix(float(1.0), fadeRef.current, isExtraStar)

    mat.colorNode = vec3(brightness)
    mat.opacityNode = fade

    const spr = new THREE.Sprite(mat)
    spr.count = maxCount
    spr.frustumCulled = false
    return spr
  }, [positions, maxCount, count])

  // Update the star size in place when the prop changes (breakpoint tier) — a
  // uniform write, no sprite/geometry rebuild.
  useEffect(() => { sizeRef.current.value = size }, [size])

  // Same for the always-visible star boundary (breakpoint tier).
  useEffect(() => { baseCountRef.current.value = Math.min(baseVisibleCount, maxCount) }, [baseVisibleCount, maxCount])

  // Ramp the second-half opacity toward the target (1 when dense / zoomed).
  const fadeTargetRef = useRef(0)
  useEffect(() => { fadeTargetRef.current = dense ? 1 : 0 }, [dense])
  useFrame((_, delta) => {
    const u = fadeRef.current
    const target = fadeTargetRef.current
    if (u.value !== target) {
      const t = 1 - Math.pow(2, -delta / 0.25) // ~0.25s ease
      u.value += (target - u.value) * t
      if (Math.abs(target - u.value) < 0.001) u.value = target
    }
  })

  // Publish the size-scale uniform so the chase camera can shrink the stars just
  // for its render (bracketed around its gl.render, then reset to 1).
  useEffect(() => {
    if (sizeScaleRef) sizeScaleRef.current = sprite.material?.userData?.sizeScaleUniform ?? null
    return () => { if (sizeScaleRef) sizeScaleRef.current = null }
  }, [sprite, sizeScaleRef])

  // Dispose the sprite's material + geometry on unmount
  useEffect(() => {
    return () => {
      sprite.material?.dispose()
      sprite.geometry?.dispose()
    }
  }, [sprite])

  // Rotate the starfield slowly and continuously — keeps turning even when
  // zoomed into a location (the sun is static there, but the stars still drift).
  useFrame((state, delta) => {
    if (!groupRef.current) return
    groupRef.current.rotation.y -= rotationSpeed * delta
  })

  return (
    <group ref={groupRef}>
      <primitive object={sprite} />
    </group>
  )
}

export default Starfield
